import { Divider } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import useOpenClose from "hooks/useOpenClose";
import PrimaryButton from "library/Atoms/Button/PrimaryButton";
import ModalLayout from "library/Layouts/ModalLayout";
import OrderSummaryBoxLayout from "library/Layouts/OrderDetailsBoxLayout/OrderSummaryBoxLayout/OrderSummaryBoxLayout";
import { isEmpty, size } from "lodash";
import useNewOrderMachine from "pages/NewOrder/hooks/useNewOrderMachine";
import ConfirmOverwriteOrderModal from "pages/NewOrder/modals/ConfirmOverwriteOrderModal/ConfirmOverwriteOrderModal";
import NewOrderDetailsSkeleton from "pages/NewOrder/skeletons/NewOrderDetailsSkeleton";
import { prettifyTaskData } from "pages/NewOrder/utils/helper";
import PropTypes from "prop-types";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { retrieveContactTimezone } from "store/features/localization.store";
import { formatDate } from "utility/date-fns";
import { prettifyPackagesAmount } from "utility/helpers/orders";
import IncreasedPriceMessage from "../NewOrderDetails/IncreasedPriceMessage/IncreasedPriceMessage";
import ApplySavedOrderForm from "./ApplySavedOrderForm/ApplySavedOrderForm";
import ImportCsvQuoteForm from "./ImportCsvQuoteForm/ImportCsvQuoteForm";

const ImportOrderForm = ({ onSubmit, onClose }) => {
  const { t } = useTranslation();

  const [{ dirty: isNewOrderFormDirty }] = useNewOrderMachine();

  const timezone = useSelector(retrieveContactTimezone);

  const importOrderForm = useFormik({
    initialStatus: null,
    initialValues: { label: "", file: null, order: null },
    onSubmit: (values) => {
      onSubmit(values);
      onClose();
    },
  });
  const {
    status,
    values: { order, file },
    submitForm,
  } = importOrderForm;
  const { id, pick_up, startDate, endDate, drops, isExpress } = order || {};

  const [isOverwriteModalOpen, openOverwriteModal, closeOverwtireModal] = useOpenClose(false);

  return (
    <FormikProvider value={importOrderForm}>
      <ModalLayout
        id="import-order-form-scroll"
        className="pb-md-4 pb-3 importScrollContainer"
        descriptionSection={
          <Trans
            i18nKey="newOrder.forms.importForm.formDescription"
            components={{
              lnk: (
                <a
                  href="https://firebasestorage.googleapis.com/v0/b/pedivan-logistic-admin.appspot.com/o/public%2Fsample.xlsx?alt=media"
                  className="text-color-primary"
                />
              ),
            }}
          />
        }
        modalControls={
          <PrimaryButton
            id="submit-order-import-btn"
            className="w-100"
            label={t("newOrder.modals.importOrderModal.submitBtn")}
            onClick={isNewOrderFormDirty ? openOverwriteModal : submitForm}
            disabled={isEmpty(order)}
          />
        }
        modals={
          isOverwriteModalOpen && <ConfirmOverwriteOrderModal onConfirm={submitForm} onClose={closeOverwtireModal} />
        }
      >
        <div className="d-flex flex-wrap">
          <div className="d-flex flex-column col-md-6 col-12 px-0">
            <ApplySavedOrderForm />

            <Divider className="mt-4 mb-3">{t("common.forms.separatorLabel")}</Divider>

            <ImportCsvQuoteForm />
          </div>

          {status === "loading" ? (
            <NewOrderDetailsSkeleton className="col-12 col-md-6 mt-4 pt-md-3 px-0 pl-md-4 pr-md-0" />
          ) : (
            !isEmpty(order) && (
              <OrderSummaryBoxLayout
                className="col-12 col-md-6 mt-4 pt-md-3 px-0 pl-md-4 pr-md-0"
                contentClassName="mt-1"
                dateSection={!isEmpty(file) && formatDate(startDate, timezone)}
                pickUpSection={prettifyTaskData(startDate, pick_up)}
                pickUpNotesSection={pick_up.notes}
                dropsSection={drops.map(({ reference, location, postcode, packages, notes }, dindex) => (
                  <OrderSummaryBoxLayout.Drops
                    key={reference}
                    titleSection={`${t("utils.ui.orderSummary.dropOff")} ${size(drops) > 1 ? `#${dindex + 1}` : ""}`}
                    dropAddressSection={prettifyTaskData(endDate, { location, postcode })}
                    packageTypeSection={prettifyPackagesAmount(packages)}
                    notesSection={notes}
                  />
                ))}
                messagesSection={<IncreasedPriceMessage startDate={startDate} isExpress={isExpress} />}
                collapse={size(drops) > 4}
                data-cy={`pre-import-${id}-order-details`}
              />
            )
          )}
        </div>
      </ModalLayout>
    </FormikProvider>
  );
};

ImportOrderForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ImportOrderForm;
